import { useState, useEffect } from 'preact/hooks';
import { getLeaves, getBalances, getMyProfile, updateMyPreferences } from '../lib/api';
import { formatLeaveValue, getUnitLabel } from '../lib/format-unit';
import { useI18n } from '../i18n/I18nContext';

// Format date for display
function formatDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateRange(start, end) {
  if (start === end) {
    return formatDate(start);
  }
  const startDate = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  if (sameYear) {
    return `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return `${formatDate(start)} - ${formatDate(end)}`;
}

// Get status badge class
function getStatusClass(status) {
  switch (status) {
    case 'approved': return 'status-approved';
    case 'pending': return 'status-pending';
    case 'rejected': return 'status-rejected';
    case 'cancelled': return 'status-cancelled';
    default: return '';
  }
}

export function ProfilePage({ user, workspace }) {
  const { t } = useI18n();
  // Get initial tab from URL query params
  const getInitialTab = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab === 'balances' || tab === 'history' || tab === 'settings') return tab;
    }
    return 'history';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // all, approved, pending

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [leavesData, balancesData, profileData] = await Promise.all([
        getLeaves({ user_id: user?.id }),
        getBalances(),
        getMyProfile()
      ]);
      // API returns array directly, sort by start_date descending (most recent first)
      const leavesArray = Array.isArray(leavesData) ? leavesData : (leavesData.leaves || []);
      const sortedLeaves = leavesArray.sort((a, b) =>
        b.start_date.localeCompare(a.start_date)
      );
      setLeaves(sortedLeaves);
      setBalances(balancesData || []);
      setProfile(profileData);
    } catch (err) {
      console.error('Failed to load profile data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSlackStatusToggle(enabled) {
    setSaving(true);
    try {
      await updateMyPreferences({ slack_status_enabled: enabled });
      setProfile(prev => ({ ...prev, slack_status_enabled: enabled ? 1 : 0 }));
    } catch (err) {
      console.error('Failed to update preference:', err);
    } finally {
      setSaving(false);
    }
  }

  // Filter leaves based on selected filter
  const filteredLeaves = leaves.filter(leave => {
    if (filter === 'all') return true;
    return leave.status === filter;
  });

  // Separate past and upcoming leaves
  const today = new Date().toISOString().split('T')[0];
  const pastLeaves = filteredLeaves.filter(l => l.end_date < today);
  const upcomingLeaves = filteredLeaves.filter(l => l.end_date >= today);

  if (loading) {
    return (
      <div class="bento-loading">
        <div class="spinner" />
      </div>
    );
  }

  return (
    <div class="profile-page">
      <div class="bento-page-header-row">
        <div>
          <h1 class="bento-page-title">My Profile</h1>
          <p class="bento-page-subtitle">View your leave history and balance</p>
        </div>
      </div>

      {/* Profile Header */}
      <div class="bento-card profile-header-card">
        <div class="profile-header">
          <div class="profile-avatar-large">
            {user?.avatar ? (
              <img src={user.avatar} alt="" />
            ) : (
              user?.name?.charAt(0) || 'U'
            )}
          </div>
          <div class="profile-info">
            <h2 class="profile-name">{user?.name}</h2>
            <p class="profile-email">{user?.email}</p>
            {user?.department_name && (
              <p class="profile-department">{user.department_name}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div class="profile-tabs">
        <button
          class={`profile-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Leave History
        </button>
        <button
          class={`profile-tab ${activeTab === 'balances' ? 'active' : ''}`}
          onClick={() => setActiveTab('balances')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
          Balances
        </button>
        <button
          class={`profile-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          Settings
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'history' && (
        <div class="profile-history">
          {/* Filter */}
          <div class="history-filter">
            <button
              class={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              class={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
              onClick={() => setFilter('approved')}
            >
              Approved
            </button>
            <button
              class={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
          </div>

          {/* Upcoming Leave */}
          {upcomingLeaves.length > 0 && (
            <div class="bento-card history-section">
              <h3 class="history-section-title">Upcoming Leave</h3>
              <div class="history-list">
                {upcomingLeaves.map((leave, i) => (
                  <LeaveHistoryItem key={i} leave={leave} />
                ))}
              </div>
            </div>
          )}

          {/* Past Leave */}
          <div class="bento-card history-section">
            <h3 class="history-section-title">Past Leave</h3>
            {pastLeaves.length > 0 ? (
              <div class="history-list">
                {pastLeaves.map((leave, i) => (
                  <LeaveHistoryItem key={i} leave={leave} />
                ))}
              </div>
            ) : (
              <div class="bento-empty">
                <p>No past leave records</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'balances' && (
        <div class="bento-card profile-balances">
          <h3 class="history-section-title">Leave Balances</h3>
          {balances.length > 0 ? (
            <div class="balances-grid">
              {balances.map((balance, i) => {
                const used = balance.used || 0;
                const entitlement = balance.entitlement || 0;
                const isUnlimited = entitlement === -1;
                const availableNum = balance.available || 0;
                const color = balance.color || '#6B7280';
                const percent = isUnlimited ? 100 : (entitlement > 0 ? ((entitlement - used) / entitlement) * 100 : 0);
                const balanceUnit = balance.unit || 'days';

                return (
                  <div key={i} class="balance-item-card">
                    <div class="balance-item-header">
                      <div class="balance-color-dot" style={{ background: color }} />
                      <span class="balance-item-name">{balance.leave_type_name}</span>
                    </div>
                    <div class="balance-item-value">
                      <span class="balance-big-number">{isUnlimited ? '∞' : availableNum}</span>
                      {!isUnlimited && <span class="balance-unit">{getUnitLabel(balanceUnit, t).charAt(0)}</span>}
                      <span class="balance-label">{isUnlimited ? 'unlimited' : 'available'}</span>
                    </div>
                    {!isUnlimited && (
                      <>
                        <div class="balance-progress-bar">
                          <div
                            class="balance-progress-fill"
                            style={{ width: `${percent}%`, background: color }}
                          />
                        </div>
                        <div class="balance-item-detail">
                          {formatLeaveValue(used, balanceUnit, t)} used of {formatLeaveValue(entitlement, balanceUnit, t)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div class="bento-empty">
              <p>No balance data available</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div class="bento-card profile-settings">
          <h3 class="history-section-title">Preferences</h3>

          {/* Slack Status Setting */}
          <div class="settings-section">
            <div class="settings-item">
              <div class="settings-item-info">
                <div class="settings-item-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z" />
                    <path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
                    <path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z" />
                    <path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z" />
                    <path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z" />
                    <path d="M15.5 19H14v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z" />
                    <path d="M10 9.5C10 8.67 9.33 8 8.5 8h-5C2.67 8 2 8.67 2 9.5S2.67 11 3.5 11h5c.83 0 1.5-.67 1.5-1.5z" />
                    <path d="M8.5 5H10V3.5C10 2.67 9.33 2 8.5 2S7 2.67 7 3.5 7.67 5 8.5 5z" />
                  </svg>
                  <span class="settings-item-title">Slack Status Updates</span>
                </div>
                <p class="settings-item-description">
                  Automatically update your Slack status when you're on leave (e.g., "On Vacation until Jan 25")
                </p>
              </div>
              <div class="settings-item-control">
                {profile?.has_slack_token ? (
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      checked={profile?.slack_status_enabled === 1}
                      onChange={(e) => handleSlackStatusToggle(e.target.checked)}
                      disabled={saving}
                    />
                    <span class="toggle-slider"></span>
                  </label>
                ) : (
                  <span class="settings-item-badge">Re-install app to enable</span>
                )}
              </div>
            </div>
            {!profile?.has_slack_token && (
              <p class="settings-note">
                To enable Slack status updates, an admin needs to re-install the Slack app to grant the required permissions.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LeaveHistoryItem({ leave }) {
  const { t } = useI18n();
  const color = leave.color || '#6B7280';
  const unit = leave.leave_type_unit || 'days';
  const duration = leave.duration_days;

  let durationDisplay;
  if (duration === 0.5 && unit === 'days') {
    durationDisplay = 'Half day';
  } else {
    durationDisplay = formatLeaveValue(duration, unit, t);
  }

  return (
    <div class="history-item">
      <div class="history-item-color" style={{ background: color }} />
      <div class="history-item-info">
        <span class="history-item-type">{leave.leave_type || leave.leave_type_name}</span>
        <span class="history-item-dates">{formatDateRange(leave.start_date, leave.end_date)}</span>
        {leave.start_time && leave.end_time && leave.start_date === leave.end_date && (
          <span class="history-item-time">{leave.start_time} - {leave.end_time}</span>
        )}
      </div>
      <div class="history-item-meta">
        <span class="history-item-duration">{durationDisplay}</span>
        <span class={`history-item-status ${getStatusClass(leave.status)}`}>
          {leave.status}
        </span>
      </div>
    </div>
  );
}
